import React from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@workspace/ui/components/Button'
import { useTranslation } from 'react-i18next'
import { toast } from '@workspace/ui/components/Sonner'
import { TemplateForm, type TemplateFormHandle } from '@/shared/components/form/TemplateForm'
import { api, type ProjectTemplateUpdate } from '@/shared/lib/api'
import type { TreeNodeData } from '@/shared/types'
import { treeNodeToTemplateFolder } from '@/shared/utils/template.utils'
import { Badge } from '@workspace/ui/components/Badge'

export const Route = createFileRoute('/templates/$id/edit')({
    component: EditTemplatePage,
})

function EditTemplatePage() {
    const { t } = useTranslation()
    const router = useRouter()
    const { id } = Route.useParams()
    const formRef = React.useRef<TemplateFormHandle>(null)

    // Fetch template data
    const {
        data: templateData,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['project-template', id],
        queryFn: () => api.projectTemplate.findOne(id),
        retry: 1,
    })

    // Show error toast if fetch fails
    React.useEffect(() => {
        if (error) {
            toast.error({
                title: t('common.status.error'),
                description: t('templates.edit.fetchError'),
            })
        }
    }, [error, t])

    // Update template mutation
    const updateMutation = useMutation({
        mutationFn: async (payload: ProjectTemplateUpdate) => {
            return await api.projectTemplate.update(id, payload)
        },
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('templates.edit.saveSuccess'),
            })
            router.navigate({ to: '/templates' })
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('templates.edit.saveError'),
            })
            console.error('Update template error:', error)
        },
    })

    const handleSubmit = (formData: {
        name: string
        description?: string
        regionId?: string[]
        areaId?: string[]
        structure: { folders: TreeNodeData[] }
        isDraft?: boolean
    }) => {
        const payload: ProjectTemplateUpdate = {
            name: formData.name,
            description: formData.description || formData.name,
            regions: formData.regionId ? formData.regionId : undefined,
            areas: formData.areaId ? formData.areaId : undefined,
            structure: {
                folders: formData.structure.folders.map((node, index) => treeNodeToTemplateFolder(node, '', index)),
            },
            isDraft: formData.isDraft,
        }

        updateMutation.mutate(payload)
    }

    const handleSaveDraft = () => {
        formRef.current?.setValue('isDraft', true)
        formRef.current?.submit()
    }

    const handleSave = () => {
        formRef.current?.setValue('isDraft', false)
        formRef.current?.submit()
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-muted-foreground">{t('common.loading')}</p>
            </div>
        )
    }

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        size="icon"
                        className="border-border-subtle"
                        onPress={() => router.history.back()}
                    >
                        <ChevronLeft className="size-5!" />
                    </Button>
                    <h1 className="text-2xl font-bold">{t('templates.edit.title')}</h1>
                    {templateData?.isDraft && (
                        <Badge className={'text-white border-none px-2 py-[2px] rounded-full bg-gray-500 -ml-1'}>
                            {t('common.draft')}
                        </Badge>
                    )}
                </div>
                <div className="flex gap-2">
                    {templateData?.isDraft && (
                        <Button
                            variant="outline"
                            size="lg"
                            onPress={handleSaveDraft}
                            isDisabled={updateMutation.isPending}
                        >
                            {t('templates.create.saveDraft')}
                        </Button>
                    )}
                    <Button
                        variant="default"
                        size="lg"
                        onPress={handleSave}
                        className={'min-w-[100px]'}
                        isDisabled={updateMutation.isPending}
                    >
                        {t('common.actions.save')}
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <TemplateForm
                ref={formRef}
                defaultValues={templateData}
                onSubmit={handleSubmit}
                isSubmitting={updateMutation.isPending}
            />
        </div>
    )
}
